/* Genex Steel — Animation Engine */

(function () {
  'use strict';

  // ── Scroll Reveal ─────────────────────────────────────────
  const revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('genex-visible');
        }
      });
    },
    { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
  );

  function initReveal() {
    // Add reveal class to sections automatically
    const targets = [
      '.card-wrapper',
      '.multicolumn-list__item',
      '.collection-list__item',
      '.image-with-text',
      '.newsletter',
      '.genex-feature-item',
    ];

    targets.forEach((selector, selectorIndex) => {
      document.querySelectorAll(selector).forEach((el, i) => {
        if (!el.classList.contains('genex-reveal')) {
          el.classList.add('genex-reveal');
          // Stagger delay based on position in group
          const delay = (i % 4) * 0.12;
          el.style.transitionDelay = `${delay}s`;
        }
        revealObserver.observe(el);
      });
    });

    // Manually tagged elements
    document.querySelectorAll('.genex-reveal, .genex-reveal-left, .genex-reveal-right').forEach((el) => {
      revealObserver.observe(el);
    });
  }

  // ── Hero image injection ──────────────────────────────────
  function initHeroImage() {
    if (!window.genexHeroSrc) return;
    const banner = document.querySelector('.banner__media');
    if (!banner) return;

    const placeholder = banner.querySelector('.placeholder-svg, svg');
    if (placeholder) placeholder.remove();

    if (!banner.querySelector('img')) {
      const img = document.createElement('img');
      img.src = window.genexHeroSrc;
      img.alt = 'Genex Steel Cookware';
      img.loading = 'eager';
      img.style.cssText = 'width:100%;height:100%;object-fit:cover;position:absolute;inset:0;';
      banner.appendChild(img);
    }
  }

  // ── Brand story image injection ───────────────────────────
  function initBrandImage() {
    if (!window.genexBrandSrc) return;

    // Target every image-with-text placeholder (brand story section has no image set)
    document.querySelectorAll('.image-with-text__media, .image-with-text__media--adapt').forEach((mediaEl) => {
      if (mediaEl.querySelector('img')) return; // already has a real image

      const placeholder = mediaEl.querySelector('svg, .placeholder-svg');
      if (placeholder) placeholder.remove();

      const img = document.createElement('img');
      img.src = window.genexBrandSrc;
      img.alt = 'Cookware that lasts generations';
      img.loading = 'lazy';
      img.style.cssText = 'width:100%;height:100%;object-fit:cover;object-position:center;display:block;';
      mediaEl.style.minHeight = '420px';
      mediaEl.style.overflow = 'hidden';
      mediaEl.appendChild(img);
    });
  }

  // ── Hero text animation ───────────────────────────────────
  function initHeroAnimation() {
    const heroHeading = document.querySelector('.banner__heading');
    const heroText = document.querySelector('.banner p');
    const heroButtons = document.querySelector('.banner .banner__buttons');

    if (heroHeading) {
      heroHeading.style.opacity = '0';
      heroHeading.style.transform = 'translateY(24px)';
      setTimeout(() => {
        heroHeading.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
        heroHeading.style.opacity = '1';
        heroHeading.style.transform = 'translateY(0)';
      }, 200);
    }
    if (heroText) {
      heroText.style.opacity = '0';
      heroText.style.transform = 'translateY(20px)';
      setTimeout(() => {
        heroText.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
        heroText.style.opacity = '1';
        heroText.style.transform = 'translateY(0)';
      }, 450);
    }
    if (heroButtons) {
      heroButtons.style.opacity = '0';
      heroButtons.style.transform = 'translateY(16px)';
      setTimeout(() => {
        heroButtons.style.transition = 'opacity 0.7s ease, transform 0.7s ease';
        heroButtons.style.opacity = '1';
        heroButtons.style.transform = 'translateY(0)';
      }, 700);
    }
  }

  // ── Image hover zoom ──────────────────────────────────────
  function initImageZoom() {
    document.querySelectorAll('.card__media').forEach((wrapper) => {
      wrapper.style.overflow = 'hidden';
      const img = wrapper.querySelector('img');
      if (img) {
        img.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
        wrapper.addEventListener('mouseenter', () => { img.style.transform = 'scale(1.07)'; });
        wrapper.addEventListener('mouseleave', () => { img.style.transform = 'scale(1)'; });
      }
    });
  }

  // ── Button ripple effect ──────────────────────────────────
  function initRipple() {
    document.querySelectorAll('.button, .product-form__submit').forEach((btn) => {
      btn.addEventListener('click', function (e) {
        const ripple = document.createElement('span');
        const rect = this.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height);
        ripple.style.cssText = `
          position: absolute;
          border-radius: 50%;
          background: rgba(255,255,255,0.3);
          width: ${size}px;
          height: ${size}px;
          left: ${e.clientX - rect.left - size / 2}px;
          top: ${e.clientY - rect.top - size / 2}px;
          transform: scale(0);
          animation: genex-ripple 0.6s linear;
          pointer-events: none;
        `;
        if (getComputedStyle(this).position === 'static') {
          this.style.position = 'relative';
        }
        this.style.overflow = 'hidden';
        this.appendChild(ripple);
        setTimeout(() => ripple.remove(), 700);
      });
    });

    // Add ripple keyframe
    if (!document.getElementById('genex-ripple-style')) {
      const style = document.createElement('style');
      style.id = 'genex-ripple-style';
      style.textContent = `
        @keyframes genex-ripple {
          to { transform: scale(2.5); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }
  }

  // ── Sticky header shadow on scroll ───────────────────────
  function initStickyHeader() {
    const header = document.querySelector('.header-wrapper');
    if (!header) return;
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          if (window.scrollY > 40) {
            header.classList.add('scrolled');
          } else {
            header.classList.remove('scrolled');
          }
          ticking = false;
        });
        ticking = true;
      }
    });
  }

  // ── Announcement bar marquee ──────────────────────────────
  function initAnnouncementPulse() {
    const bar = document.querySelector('.announcement-bar, .utility-bar');
    if (!bar) return;
    if (bar.querySelector('.genex-marquee-wrapper')) return;

    // Source the offers from Dawn's announcement blocks so they stay editable
    // in the theme editor instead of drifting out of date in here.
    const offers = [...bar.querySelectorAll('.announcement-bar__message')]
      .map((el) => el.textContent.trim())
      .filter(Boolean);
    if (!offers.length) return;

    // Wipe out Dawn's slideshow content entirely
    bar.innerHTML = '';

    const wrapper = document.createElement('div');
    wrapper.className = 'genex-marquee-wrapper';
    const track = document.createElement('div');
    track.className = 'genex-marquee-track';

    // Duplicate for seamless loop
    [...offers, ...offers].forEach((m) => {
      const item = document.createElement('span');
      item.className = 'genex-marquee-item';
      item.textContent = m;
      track.appendChild(item);
    });

    wrapper.appendChild(track);
    bar.appendChild(wrapper);
  }

  // ── Best Sellers: BESTSELLER badge + % OFF badge ──────────
  // BADGES TEMPORARILY DISABLED — re-enable by uncommenting below
  function initBestSellerBadges() {
    const sections = document.querySelectorAll('.genex-featured-collection');
    if (!sections.length) return;

    sections.forEach((section) => {
      /* BESTSELLER badge — disabled
      section.querySelectorAll('.card__inner').forEach((inner) => {
        if (!inner.querySelector('.genex-bs-badge')) {
          const badge = document.createElement('div');
          badge.className = 'genex-bs-badge';
          badge.textContent = 'Bestseller';
          inner.appendChild(badge);
        }
      });
      */

      // Replace Dawn's "Sale" text with "X% OFF"
      section.querySelectorAll('.card__badge .badge').forEach((badge) => {
        const wrapper = badge.closest('.card-wrapper') || badge.closest('.grid__item');
        if (!wrapper) return;
        const saleEl = wrapper.querySelector('.price-item--sale');
        const compareEl = wrapper.querySelector('.price-item--regular');
        if (saleEl && compareEl) {
          const parse = (el) => parseFloat(el.textContent.replace(/[^\d.]/g, ''));
          const sale = parse(saleEl);
          const compare = parse(compareEl);
          if (compare > 0 && sale > 0 && compare > sale) {
            const pct = Math.round((1 - sale / compare) * 100);
            badge.textContent = pct + '% OFF';
          }
        }
      });
    });
  }

  // ── Counter animation (for stats if present) ─────────────
  function animateCounter(el) {
    const target = parseInt(el.dataset.target || el.textContent, 10);
    if (isNaN(target)) return;
    let start = 0;
    const duration = 1400;
    const step = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      el.textContent = Math.floor(progress * target).toLocaleString();
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  const counterObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !entry.target.dataset.counted) {
        entry.target.dataset.counted = 'true';
        animateCounter(entry.target);
      }
    });
  }, { threshold: 0.5 });

  document.querySelectorAll('.genex-counter').forEach((el) => counterObserver.observe(el));

  // ── Clean product card titles ─────────────────────────────
  function initCardTitles() {
    document.querySelectorAll('.card__heading a').forEach((el) => {
      let text = el.textContent.trim();
      // Strip "Genex Steel " brand prefix
      text = text.replace(/^Genex\s+Steel\s+/i, '');
      // Cut at first " | " pipe separator
      const pipe = text.indexOf(' | ');
      if (pipe > 0) text = text.substring(0, pipe);
      el.textContent = text;
    });
  }

  // ── Product page: savings badge + promo boxes + stock bar ─
  async function initProductPageEnhancements() {
    const priceBlock = document.querySelector('.product__price, .price__container');
    if (!priceBlock) return;

    const handle = window.location.pathname.match(/\/products\/([^/?#]+)/)?.[1];
    if (!handle) return;

    let product;
    try { product = await fetch(`/products/${handle}.js`).then(r => r.json()); }
    catch { return; }

    const paramId = new URLSearchParams(window.location.search).get('variant');
    let variant = (paramId && product.variants.find(v => String(v.id) === paramId))
      || product.variants[0];
    if (!variant) return;

    const fmt = c => '₹' + (c / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });

    // ── 1. Savings badge next to price ──
    if (variant.compare_at_price > variant.price) {
      const saved = variant.compare_at_price - variant.price;
      const pct   = Math.round((saved / variant.compare_at_price) * 100);

      if (!document.querySelector('.genex-savings-badge')) {
        const badge = document.createElement('span');
        badge.className = 'genex-savings-badge';
        badge.textContent = `Save ${fmt(saved)} (${pct}% off)`;
        const priceRow = document.querySelector('.product__price .price__sale, .product__price .price');
        if (priceRow) priceRow.appendChild(badge);
      }
      if (!document.querySelector('.genex-tax-note')) {
        const note = document.createElement('p');
        note.className = 'genex-tax-note';
        note.textContent = '(Inclusive of all taxes)';
        priceBlock.after(note);
      }

      // ── 2. Promo boxes (shipping threshold + gift) ──
      const basePrice   = variant.price / 100;
      const FREE_SHIP   = 1999;
      const GIFT_MIN    = 4999;
      const shipAdd     = Math.max(0, FREE_SHIP - basePrice);
      const giftAdd     = Math.max(0, GIFT_MIN - basePrice);

      if (!document.querySelector('.genex-promo-box')) {
        const rows = [];
        rows.push(shipAdd > 0
          ? `<div class="genex-promo-row"><span class="icon">🚚</span><span>Add ₹${shipAdd.toLocaleString('en-IN')} more for FREE shipping.</span></div>`
          : `<div class="genex-promo-row"><span class="icon">🚚</span><span>FREE shipping on this order.</span></div>`);

        rows.push(giftAdd > 0
          ? `<div class="genex-promo-row"><span class="icon">🎁</span><span>Add ₹${giftAdd.toLocaleString('en-IN')} more for a FREE gift worth ₹999.</span></div>`
          : `<div class="genex-promo-row"><span class="icon">🎁</span><span>Includes a FREE gift worth ₹999.</span></div>`);

        const box = document.createElement('div');
        box.className = 'genex-promo-box';
        box.innerHTML = rows.join('');

        const formEl = document.querySelector('.product-form');
        if (formEl) formEl.before(box);
      }
    } else {
      if (!document.querySelector('.genex-tax-note')) {
        const note = document.createElement('p');
        note.className = 'genex-tax-note';
        note.textContent = '(Inclusive of all taxes)';
        priceBlock.after(note);
      }
    }

    // ── 3. Stock progress bar ──
    const qty = variant.inventory_quantity;
    if (qty > 0 && qty <= 15 && !document.querySelector('.genex-stock-wrap')) {
      const fillPct = Math.min(100, Math.round((qty / 15) * 100));
      const wrap = document.createElement('div');
      wrap.className = 'genex-stock-wrap';
      wrap.innerHTML = `
        <div class="genex-stock-bar"><div class="genex-stock-fill" style="width:${fillPct}%"></div></div>
        <p class="genex-stock-text">Only ${qty} left — order soon!</p>`;
      const variantPicker = document.querySelector('variant-radios, .product-form__input--pill');
      if (variantPicker) variantPicker.after(wrap);
    }
  }

  // ── Variant size cards with price ────────────────────────
  async function initVariantPricePills() {
    const picker = document.querySelector('variant-radios');
    if (!picker) return;

    const handle = window.location.pathname.match(/\/products\/([^/?#]+)/)?.[1];
    if (!handle) return;

    let product;
    try {
      product = await fetch(`/products/${handle}.js`).then((r) => r.json());
    } catch { return; }

    if (!product.variants || product.variants.length <= 1) return;

    // Find the Size option index
    const sizeIndex = product.options.findIndex((o) =>
      /size|litre|ltr|liter|capacity|volume/i.test(o)
    );
    if (sizeIndex === -1) return;

    // Map each option value → its variant (lowest price if duplicates)
    const variantMap = {};
    for (const v of product.variants) {
      const key = v.options[sizeIndex];
      if (!variantMap[key] || v.price < variantMap[key].price) {
        variantMap[key] = v;
      }
    }

    // Format price in INR
    const fmt = (cents) =>
      '₹' + (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 0 });

    // Update every size radio label
    const sizeOptionName = product.options[sizeIndex];
    picker.querySelectorAll(`input[name="${sizeOptionName}"]`).forEach((input) => {
      const label = picker.querySelector(`label[for="${input.id}"]`);
      if (!label) return;
      const v = variantMap[input.value];
      if (!v) return;

      const subtitle = product.options.length > 1
        ? v.options.filter((_, i) => i !== sizeIndex).join(' · ')
        : '';

      label.innerHTML = `
        <span class="genex-vpill-size">${input.value}</span>
        ${subtitle ? `<span class="genex-vpill-sub">${subtitle}</span>` : ''}
        <span class="genex-vpill-price">${fmt(v.price)}</span>
      `;

      if (!v.available) label.classList.add('disabled');
    });
  }

  // ── Equal card heights ────────────────────────────────────
  function equalizeCards() {
    document.querySelectorAll('.product-grid').forEach((grid) => {
      const items = [...grid.querySelectorAll('.grid__item')];
      if (!items.length) return;

      // Reset so we measure natural heights
      items.forEach((item) => {
        const w = item.querySelector('.card-wrapper');
        const c = item.querySelector('.card');
        if (w) w.style.height = '';
        if (c) c.style.height = '';
      });

      // Group items into rows by their offsetTop within the grid
      const rowMap = new Map();
      items.forEach((item) => {
        const top = item.offsetTop;
        if (!rowMap.has(top)) rowMap.set(top, []);
        rowMap.get(top).push(item);
      });

      // For each row: equalise wrapper heights, then stretch the inner card
      rowMap.forEach((rowItems) => {
        const wrappers = rowItems.map((i) => i.querySelector('.card-wrapper')).filter(Boolean);
        const maxH = Math.max(...wrappers.map((w) => w.offsetHeight));
        wrappers.forEach((w) => {
          w.style.height      = maxH + 'px';
          w.style.display     = 'flex';
          w.style.flexDirection = 'column';
          const card = w.querySelector('.card');
          if (card) {
            card.style.flex   = '1';
            card.style.height = 'auto';
          }
        });
      });
    });
  }

  // ── Init all ──────────────────────────────────────────────
  function init() {
    initReveal();
    initHeroImage();
    initBrandImage();
    initCardTitles();
    initProductPageEnhancements();
    initHeroAnimation();
    initImageZoom();
    initRipple();
    initStickyHeader();
    initAnnouncementPulse();
    initVariantPricePills();
    initBestSellerBadges();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Run equalizeCards ONLY after all images have loaded and layout is stable
  window.addEventListener('load', () => {
    initCardTitles();
    // Double RAF ensures the browser has painted and settled
    requestAnimationFrame(() => requestAnimationFrame(equalizeCards));
  });

  // Re-equalise on resize
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      requestAnimationFrame(equalizeCards);
    }, 200);
  });

  // Re-run on Shopify section re-render
  document.addEventListener('shopify:section:load', () => {
    setTimeout(init, 100);
  });
})();
